import {useCallback, useEffect, useRef, useState} from "react";
import {setupDefaultNotification} from "@agir/notifications/common/api";

import logger from "@agir/lib/utils/logger";
import {useLocalStorage} from "@agir/lib/utils/hooks";
import {useMobileApp} from "@agir/front/app/hooks/useMobileApp";
import { LocationAuthorizationStatus } from "../../../carte/components/common/location/constants";

const log = logger(__filename);

window.iOSNativeMessage = (data) => {
  let event = new CustomEvent("iOSMessage", {
    detail: {
      data: JSON.parse(data),
    },
  });

  log.debug("got message", data);

  window.dispatchEvent(event);
};

export const iosAction= {
  SET_NOTIFICATION_STATE: 'setNotificationState',
  GET_NOTIFICATION_STATE: 'getNotificationState',
  ENABLE_NOTIFICATIONS: 'enableNotifications',

  SET_LOCATION_STATE: 'setLocationState',
  GET_LOCATION_STATE: 'getLocationState',

  OPEN_APPLICATION_SETTINGS: 'openApplicationSettings'
};

export const useIOSNotificationGrant = (onNotificationGrant) => {
  const [notificationIsGranted, setNotificationIsGranted] = useState(false)
  const [fcmToken] = useLocalStorage("AP_FCMToken", null);

  const iosMessageHandler = useCallback(async ({action, noPermission}) => {
    if (action === iosAction.SET_NOTIFICATION_STATE && noPermission === "false") {
      setNotificationIsGranted(true);
      onNotificationGrant?.()
    }
  }, []);

  const postMessage = useIOSMessages(iosMessageHandler);

  useEffect(() => {
    postMessage && postMessage({ action: iosAction.GET_NOTIFICATION_STATE });
  }, [postMessage]);

  const grantNotification = useCallback(() => {
    if (postMessage) {
      postMessage && postMessage({ action: iosAction.ENABLE_NOTIFICATIONS });
    } else {
      console.error("postMessage not found !")
    }
  }, [postMessage])

  return {
    grantNotification,
    notificationIsGranted,
    hasUpdate: fcmToken !== null
  }
}

export const useIOSMessages = (cb) => {
  const savedCallback = useRef();

  useEffect(() => {
    savedCallback.current = cb;
  }, [cb]);

  useEffect(() => {
    const eventListener = (event) =>
      savedCallback.current && savedCallback.current(event.detail.data);

    window.addEventListener("iOSMessage", eventListener);

    return () => {
      window.removeEventListener("iOSMessage", eventListener);
    };
  }, []);

  const postMessage = useCallback((data) => {
    window.webkit?.messageHandlers?.main?.postMessage(data);
  }, []);

  if (!window.webkit?.messageHandlers?.main) {
    return;
  }

  return postMessage;
};

/**
 * 
 * @param {callback} onLocationGrant 
 * @returns {
 *  {LocationAuthorizationStatus | false} locationIsGranted
 *  {bool} hasUpdate
 * }
 */

export function useIOSLocationGrant(onLocationGrant) {
    const [iosLocationGranted, setIosLocationGranted] = useState(LocationAuthorizationStatus.NOT_DETERMINED)
    const [hasLocationEnabled, setHasLocationEnabled] = useState(false)
    const {isIOS} = useMobileApp();
   
    const iosMessageHandler = useCallback(async (body) => {
        const {action, state} = body

      if (action === iosAction.SET_LOCATION_STATE) {
        setHasLocationEnabled(true)
        let newState = LocationAuthorizationStatus.NOT_DETERMINED
        if (state === LocationAuthorizationStatus.GRANTED)
          newState = LocationAuthorizationStatus.GRANTED
        else if (state === LocationAuthorizationStatus.DENIED)
          newState = LocationAuthorizationStatus.DENIED
        setIosLocationGranted(newState);
        onLocationGrant?.(newState)
      }
    }, []);

    const postMessage = useIOSMessages(iosMessageHandler);

    const updateLocationIsGranted = useCallback(() => {
      if (postMessage)
        postMessage({ action: iosAction.GET_LOCATION_STATE });
    }, [postMessage])

    useEffect(() => {
      updateLocationIsGranted()
    }, [updateLocationIsGranted]);

    return {
        locationIsGranted: isIOS && iosLocationGranted,
        updateLocationIsGranted: isIOS && updateLocationIsGranted,
        hasUpdate: hasLocationEnabled,
    }
}